import React, { useState, useRef } from 'react';
import { parseNlq } from '../../api';
import { mk } from '../../utils';

const EXAMPLES = [
  'paid Netflix $18',
  'spent $47 at Trader Joe\'s',
  'got paid $2,400',
  'saved $200',
];

function applyAction(action, state, updateState, showToast) {
  const now = new Date();
  const k = mk(now.getFullYear(), now.getMonth());

  switch (action.action) {
    case 'log_expense': {
      const catId = action.catId || 'other';
      const place = action.place || 'Quick entry';
      const amt = parseFloat(action.amt) || 0;
      if (!amt) { showToast('No amount found — try again'); return; }
      const vs = { ...(state.varSpend || {}) };
      if (!vs[k]) vs[k] = {};
      if (!vs[k][catId]) vs[k][catId] = [];
      vs[k] = { ...vs[k], [catId]: [...vs[k][catId], { place, amt, ts: Date.now(), date: new Date().toISOString().slice(0, 10) }] };
      updateState({ varSpend: vs });
      showToast(`✓ Logged $${amt.toFixed(2)} for ${place}`);
      break;
    }
    case 'log_income': {
      const amt = parseFloat(action.amt) || 0;
      if (!amt) { showToast('No amount found — try again'); return; }
      const date = action.date || now.toISOString().slice(0, 10);
      const entry = { id: 'inc_' + Date.now(), amt, date };
      const log = [...(state.incomeLog || []), entry].sort((a, b) => a.date.localeCompare(b.date));
      updateState({ incomeLog: log });
      showToast(`💰 Income logged: $${amt.toFixed(2)}`);
      break;
    }
    case 'log_savings': {
      const amt = parseFloat(action.amt) || 0;
      if (!amt) { showToast('No amount found — try again'); return; }
      const date = action.date || now.toISOString().slice(0, 10);
      const deps = { ...(state.savDeposits || {}) };
      if (!deps[k]) deps[k] = [];
      deps[k] = [...deps[k], { amt, date }];
      updateState({ savDeposits: deps });
      showToast(`💵 Savings logged: $${amt.toFixed(2)}`);
      break;
    }
    case 'mark_bill_paid': {
      const billId = action.billId;
      const billName = action.billName || 'Bill';
      const amt = action.amt || true;
      if (!billId) { showToast("Couldn't match that to a bill — check your bills list"); return; }
      const pb = { ...(state.paidBills || {}) };
      if (!pb[k]) pb[k] = {};
      pb[k] = { ...pb[k], [billId]: amt };
      updateState({ paidBills: pb });
      showToast(`✅ ${billName} marked paid`);
      break;
    }
    case 'unknown': {
      showToast(action.hint || "Couldn't understand that — try a different phrasing");
      break;
    }
    default:
      showToast("Couldn't understand that — try again");
  }
}

export function QuickEntryBar({ state, updateState, showToast, cats, allBills, pd }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [exampleIdx] = useState(() => Math.floor(Math.random() * EXAMPLES.length));
  const inputRef = useRef();

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    setLoading(true);
    setText('');

    const unpaidBills = (allBills || []).filter((b) => !pd?.[b.id]).map((b) => ({
      id: b.id,
      name: b.name,
      amt: b.amt,
    }));

    const res = await parseNlq(trimmed, {
      categories: (cats || []).map((c) => ({ id: c.id, name: c.name })),
      bills: unpaidBills,
    });

    setLoading(false);

    if (res.error) {
      showToast(res.error);
      setText(trimmed);
      return;
    }

    if (res.action) {
      applyAction(res.action, state, updateState, showToast);
    }

    inputRef.current?.focus();
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        marginBottom: 14,
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        padding: '10px 14px',
        background: 'var(--surf)',
        border: '1px solid var(--bdr)',
        borderRadius: 13,
      }}
    >
      <span style={{ fontSize: 18, flexShrink: 0, lineHeight: 1 }}>⚡</span>
      <input
        ref={inputRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`e.g. "${EXAMPLES[exampleIdx]}"`}
        disabled={loading}
        style={{
          flex: 1,
          background: 'none',
          border: 'none',
          outline: 'none',
          color: 'var(--txt)',
          fontSize: 13,
          fontFamily: 'inherit',
          minWidth: 0,
        }}
      />
      <button
        type="submit"
        disabled={loading || !text.trim()}
        style={{
          flexShrink: 0,
          padding: '6px 13px',
          borderRadius: 8,
          border: 'none',
          background: text.trim() && !loading ? 'var(--ylw)' : 'var(--surf2)',
          color: text.trim() && !loading ? '#000' : 'var(--mut)',
          fontWeight: 700,
          fontSize: 12,
          cursor: text.trim() && !loading ? 'pointer' : 'default',
          fontFamily: 'inherit',
          transition: 'background .15s',
          whiteSpace: 'nowrap',
        }}
      >
        {loading ? '...' : 'Log it →'}
      </button>
    </form>
  );
}
